// Service Requests page for Sunlight Grounds Care Client Portal
import { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '@/components/layout/Header';
import api from '@/api/client';
import { useProperty } from '@/hooks/useProperty';
import { Plus, ChevronDown, X, Clock, CheckCircle, ArrowRight, AlertCircle, Filter } from 'lucide-react';

const STATUS_CONFIG = {
  pending: { label: 'Pending', bg: 'bg-gold-light', text: 'text-[#8B6A10]', icon: Clock },
  in_progress: { label: 'In Progress', bg: 'bg-navy-light', text: 'text-navy', icon: ArrowRight },
  completed: { label: 'Completed', bg: 'bg-green-light', text: 'text-green', icon: CheckCircle },
  cancelled: { label: 'Cancelled', bg: 'bg-slate-light', text: 'text-slate', icon: AlertCircle },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] font-dm font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}>
      <Icon size={10} />
      {config.label}
    </span>
  );
}

export default function Requests({ user }) {
  const { selectedProperty } = useProperty();
  const location = useLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [filter, setFilter] = useState('all');

  // New request form
  const [showForm, setShowForm] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const [selectedLocation, setSelectedLocation] = useState('');
  const [customLocation, setCustomLocation] = useState('');
  const [preferredDate, setPreferredDate] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedProperty) {
      setLoading(false);
      return;
    }
    try {
      const [reqs, types, locs] = await Promise.all([
        api.getServiceRequests(selectedProperty.id),
        api.getServiceRequestTypes(selectedProperty.id),
        api.getLocations(selectedProperty.id),
      ]);
      setRequests(reqs);
      setRequestTypes(types);
      setLocations(locs);
    } catch (err) {
      console.error('Failed to load requests:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-open form if navigated with state
  useEffect(() => {
    if (location.state?.openNewRequest) {
      setShowForm(true);
      // Prefill type if provided (e.g., "Schedule a Walkthrough")
      if (location.state?.prefillType && requestTypes.length > 0) {
        const match = requestTypes.find(t => 
          t.name.toLowerCase().includes(location.state.prefillType.toLowerCase())
        );
        if (match) {
          setSelectedType(match.id);
        } else {
          // If no matching type, set description with the requested type
          setDescription(location.state.prefillType);
        }
      }
    }
  }, [location.state, requestTypes]);

  const allLocations = [...(locations.zones || []), ...(locations.rooms || [])];

  const filtered = filter === 'all'
    ? requests
    : requests.filter(r => r.status === filter);

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === 'pending').length,
    in_progress: requests.filter(r => r.status === 'in_progress').length,
    completed: requests.filter(r => r.status === 'completed').length,
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const locationText = selectedLocation === '__other__' ? customLocation : selectedLocation;
    if (!selectedType || !locationText || !selectedProperty) return;

    setSubmitting(true);
    try {
      const typeName = requestTypes.find(t => t.id === selectedType)?.name || selectedType;
      const newReq = await api.createServiceRequest({
        property_id: selectedProperty.id,
        request_type: typeName,
        service_type: selectedType,
        location_text: locationText,
        description: description || `Preferred date: ${preferredDate || 'Flexible'}`,
      });

      // Add to local list with display fields
      setRequests(prev => [{
        ...newReq,
        request_type: typeName,
        location_text: locationText,
        preferred_date: preferredDate,
        description: description,
      }, ...prev]);

      // Reset form
      setShowForm(false);
      setSelectedType('');
      setSelectedLocation('');
      setCustomLocation('');
      setPreferredDate('');
      setDescription('');
    } catch (err) {
      console.error('Failed to create request:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const safe = dateStr.includes('T') ? dateStr : dateStr + 'T12:00:00';
    const d = new Date(safe);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-light">
        <Header title="Requests" property={selectedProperty} />
        <div className="p-4 space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="sg-card p-4 animate-pulse">
              <div className="h-3 bg-slate/20 rounded w-32 mb-3"></div>
              <div className="h-3 bg-slate/20 rounded w-48 mb-2"></div>
              <div className="h-3 bg-slate/20 rounded w-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-light pb-24" data-testid="requests-page">
      <Header title="Requests" property={selectedProperty} />

      <div className="p-4 sm:p-6 md:p-8 max-w-4xl mx-auto">
        {/* New Request Button */}
        <button
          onClick={() => setShowForm(true)}
          className="w-full flex items-center justify-center gap-2 bg-navy text-white font-dm text-[12px] font-medium py-3 rounded-xl hover:bg-navy-mid transition-colors mb-4"
          data-testid="new-request-btn"
        >
          <Plus size={16} />
          New service request
        </button>

        {/* Filter Chips */}
        <div className="flex gap-2 overflow-x-auto scrollbar-hide mb-4" data-testid="request-filters">
          {[
            { key: 'all', label: 'All' },
            { key: 'pending', label: 'Pending' },
            { key: 'in_progress', label: 'In Progress' },
            { key: 'completed', label: 'Completed' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-dm text-[10px] font-bold transition-colors ${
                filter === f.key
                  ? 'bg-navy text-white border-navy'
                  : 'bg-cream text-navy border-black/[0.12] hover:border-navy/30'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
              <span className={`text-[9px] px-1 py-0.5 rounded-full ${
                filter === f.key ? 'bg-white/20' : 'bg-slate-light'
              }`}>
                {counts[f.key] || 0}
              </span>
            </button>
          ))}
        </div>

        {/* Request List */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="requests-list">
            {filtered.map((req, idx) => (
              <div
                key={req.id || idx}
                className="sg-card p-4"
                data-testid={`request-item-${req.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="font-dm text-xs font-bold text-navy">
                      {req.request_type}
                    </p>
                    {req.location_text && (
                      <p className="font-dm text-[10px] text-slate mt-0.5">
                        {req.location_text}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={req.status} />
                </div>

                {req.description && (
                  <p className="font-dm text-[10px] text-slate leading-relaxed mb-2 line-clamp-2">
                    {req.description}
                  </p>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-black/[0.06]">
                  <p className="font-dm text-[9px] text-slate">
                    {req.preferred_date && `Preferred: ${formatDate(req.preferred_date)}`}
                    {req.preferred_date && req.created_at && ' · '}
                    {req.created_at && `Submitted ${formatDate(req.created_at)}`}
                  </p>
                  {req.status === 'pending' && (
                    <span className="font-dm text-[9px] text-gold font-medium">Awaiting review</span>
                  )}
                  {req.status === 'in_progress' && req.assigned_to && (
                    <span className="font-dm text-[9px] text-navy font-medium">Assigned: {req.assigned_to}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="sg-card p-8 text-center" data-testid="no-requests">
            <div className="w-12 h-12 rounded-full bg-slate-light flex items-center justify-center mx-auto mb-3">
              <Filter size={20} className="text-slate" />
            </div>
            <p className="font-dm text-xs font-medium text-navy mb-1">
              {filter === 'all' ? 'No requests yet' : `No ${filter.replace('_', ' ')} requests`}
            </p>
            <p className="font-dm text-[10px] text-slate">
              {filter === 'all'
                ? 'Tap the button above to submit your first service request'
                : 'Try a different filter to see your requests'}
            </p>
          </div>
        )}
      </div>

      {/* New Request Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[99998] flex items-end" data-testid="new-request-modal">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowForm(false)}
          />
          {/* Sheet */}
          <div className="relative w-full bg-cream rounded-t-2xl max-h-[80vh] overflow-y-auto animate-slide-up">
            <div className="sticky top-0 bg-cream px-5 pt-4 pb-2 border-b border-black/[0.06] flex items-center justify-between">
              <h3 className="font-dm text-sm font-bold text-navy">New Service Request</h3>
              <button
                onClick={() => setShowForm(false)}
                className="w-8 h-8 rounded-full bg-slate-light flex items-center justify-center hover:bg-slate/20 transition-colors"
                data-testid="close-request-modal-btn"
              >
                <X size={16} className="text-slate" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              {/* Service Type */}
              <div>
                <label className="sg-label mb-1.5 block">Service type</label>
                <div className="relative">
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full px-3 py-3 rounded-xl border border-black/[0.08] bg-white font-dm text-[12px] text-navy appearance-none cursor-pointer focus:outline-none focus:border-navy/30"
                    required
                    data-testid="request-service-type"
                  >
                    <option value="">Select service type</option>
                    {requestTypes.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
                </div>
              </div>

              {/* Location */}
              <div>
                <label className="sg-label mb-1.5 block">Area / Zone</label>
                <div className="relative">
                  <select
                    value={selectedLocation}
                    onChange={(e) => { setSelectedLocation(e.target.value); if (e.target.value !== '__other__') setCustomLocation(''); }}
                    className="w-full px-3 py-3 rounded-xl border border-black/[0.08] bg-white font-dm text-[12px] text-navy appearance-none cursor-pointer focus:outline-none focus:border-navy/30"
                    required
                    data-testid="request-location"
                  >
                    <option value="">Select area</option>
                    {allLocations.map(loc => (
                      <option key={loc.id} value={loc.name}>{loc.name}</option>
                    ))}
                    <option value="__other__">Other</option>
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate pointer-events-none" />
                </div>
                {selectedLocation === '__other__' && (
                  <input
                    type="text"
                    value={customLocation}
                    onChange={(e) => setCustomLocation(e.target.value)}
                    placeholder="Enter area name"
                    className="w-full mt-2 px-3 py-3 rounded-xl border border-black/[0.08] bg-white font-dm text-[12px] text-navy focus:outline-none focus:border-navy/30"
                    data-testid="request-location-custom"
                  />
                )}
              </div>

              {/* Preferred Date */}
              <div>
                <label className="sg-label mb-1.5 block">Preferred date</label>
                <input
                  type="date"
                  value={preferredDate}
                  onChange={(e) => setPreferredDate(e.target.value)}
                  className="w-full px-3 py-3 rounded-xl border border-black/[0.08] bg-white font-dm text-[12px] text-navy focus:outline-none focus:border-navy/30"
                  data-testid="request-date"
                />
              </div>

              {/* Description */}
              <div>
                <label className="sg-label mb-1.5 block">Notes (optional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Any details or special instructions..."
                  rows={3}
                  className="w-full px-3 py-3 rounded-xl border border-black/[0.08] bg-white font-dm text-[12px] text-navy placeholder:text-slate/50 focus:outline-none focus:border-navy/30 resize-none"
                  data-testid="request-description"
                />
              </div>

              <button
                type="submit"
                disabled={submitting || !selectedType || !selectedLocation || (selectedLocation === '__other__' && !customLocation)}
                className="w-full bg-navy text-white font-dm text-[12px] font-medium py-3 rounded-xl hover:bg-navy-mid transition-colors disabled:opacity-50"
                data-testid="submit-new-request-btn"
              >
                {submitting ? 'Submitting...' : 'Submit request'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
